import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { useUIStore } from '@/state/uiStore';
import { aud, customerName, simTimeFull } from '@/utils/format';
import { Search, Download, RotateCw, Banknote, History, Undo2, Mail } from 'lucide-react';
import { format } from 'date-fns';
import clsx from 'clsx';
import ManualDepositModal from '@/components/ManualDepositModal';
import DepositMovementDrawer from '@/components/DepositMovementDrawer';
import { resendDepositLink, reinstateDeposit, isEffectivelyExpired } from '@/domain/depositFlow';
import { routeExpiredResend } from '@/domain/resendRoutingFlow';
import { canReinstateLapsedDeposit } from '@/domain/permissions';
import type { CustomerRecord, Deposit, StudioId } from '@/domain/types';
import {
  deriveDepositStatus,
  depositOrigin,
  DEPOSIT_STATUS_META,
  type DepositDisplayStatus,
} from '@/domain/depositStatus';
import { AWAITING_CLIENT } from '@/domain/selfBookFlow';
import { buildTopupTrack, sendTopupReminder, type TopupTrackState } from '@/domain/topupReminders';

const METHOD_LABEL: Record<string, string> = {
  eftpos: 'EFTPOS',
  cash: 'Cash',
  'bank-transfer': 'Bank transfer',
};

// 'topups' is a synthetic view (partial deposits derived from the ledger), not a
// stored DepositDisplayStatus — handled like the existing synthetic 'pending'.
type DepositFilterKey = DepositDisplayStatus | 'all' | 'pending' | 'topups';

const FILTERS: Array<{ key: DepositFilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'sent', label: 'Sent' },
  { key: 'viewed', label: 'Viewed' },
  { key: 'paid', label: 'Paid' },
  { key: 'topups', label: 'Top-ups' },
  { key: 'placed', label: 'Placed' },
  { key: 'lapsed', label: 'Lapsed' },
  { key: 'forfeited', label: 'Forfeited' },
  { key: 'expired', label: 'Expired' },
];

export default function DepositsLedger() {
  const navigate = useNavigate();
  const deposits = useBrainStore((s) => s.deposits);
  const customers = useBrainStore((s) => s.customers);
  const studios = useBrainStore((s) => s.studios);
  const artists = useBrainStore((s) => s.artists);
  const bookings = useBrainStore((s) => s.bookings);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const pushNotification = useUIStore((s) => s.pushNotification);
  // C9: an artist/apprentice viewing the ledger only ever sees their own
  // deposits, and can't resend links or place bookings from here.
  const persona = useSessionStore((s) => s.persona);
  // Apprentices are Artist records too, but the session stashes their id under
  // apprenticeId, so fall back to it or the self-filter matches nothing.
  const sessionArtistId = useSessionStore((s) => s.artistId ?? s.apprenticeId);
  const readOnly = persona === 'artist' || persona === 'apprentice';

  // Resend the Stripe link straight from the ledger row — the bookings manager
  // lives here, not on the customer card. Mints a fresh link + hold via the
  // object form of resendDepositLink (reusing the discussed slot) and stamps the
  // old record supersededByDepositId + resentAt so it reads "link resent". No-op
  // unless the source deposit has effectively expired, so a stray click on a
  // live row does nothing and shows no success toast. Handles customer-less
  // self-book rows (AWAITING_CLIENT) without crashing.
  const resendLink = (row: { customer?: CustomerRecord; deposit: Deposit }) => {
    const who = row.customer ? customerName(row.customer) : 'Awaiting client';
    // Expired links route through the availability check (Prompt I1): same date if
    // still free, else the customer is pushed to the top of the money queue.
    if (isEffectivelyExpired(row.deposit)) {
      const result = routeExpiredResend(row.deposit.id);
      if (!result) return;
      if (result.outcome === 'resent') {
        pushNotification({
          intensity: 'info',
          title: 'Link resent (same date)',
          body: `${who} · original date still free`,
          ttlMs: 4000,
          customerId: row.customer?.id,
        });
      } else {
        pushNotification({
          intensity: 'high',
          title: 'Date taken — pushed to Money',
          body: `${who} · top of the money queue, rebook needed`,
          ttlMs: 5000,
          customerId: row.customer?.id,
        });
      }
      return;
    }
    const fresh = resendDepositLink({ depositId: row.deposit.id });
    if (!fresh) return;
    pushNotification({
      intensity: 'info',
      title: 'New link sent, old marked resent',
      body: `${who} · fresh link sent`,
      ttlMs: 4000,
      customerId: row.customer?.id,
    });
  };

  // Customer-less rows: a self-book held/pending deposit is stamped with
  // AWAITING_CLIENT until a client claims the link. Show a placeholder name
  // rather than dropping the row or crashing on a missing customer record.
  const rowName = (row: { customer?: CustomerRecord }) =>
    row.customer ? customerName(row.customer) : 'Awaiting client';

  const studioList = Object.values(studios);
  const [studioFilter, setStudioFilter] = useState<StudioId | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<DepositFilterKey>('all');
  const [search, setSearch] = useState('');
  const [logOpen, setLogOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const canReinstate = persona ? canReinstateLapsedDeposit(persona) : false;

  const nowMs = new Date(clockIso).getTime();

  const enriched = useMemo(() => {
    return Object.values(deposits)
      .map((d) => ({
        deposit: d,
        status: deriveDepositStatus(d, nowMs, d.bookingId ? bookings[d.bookingId] : undefined),
        customer: customers[d.customerId],
      }))
      .filter((row) => row.customer || row.deposit.customerId === AWAITING_CLIENT)
      .filter((row) => !readOnly || row.deposit.artistId === sessionArtistId)
      .sort(
        (a, b) =>
          new Date(b.deposit.createdAt).getTime() - new Date(a.deposit.createdAt).getTime(),
      );
  }, [deposits, customers, bookings, nowMs, readOnly, sessionArtistId]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return enriched.filter((row) => {
      if (studioFilter !== 'all' && row.deposit.studioId !== studioFilter) return false;
      if (statusFilter === 'pending') {
        if (row.status !== 'sent' && row.status !== 'viewed') return false;
      } else if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      if (needle) {
        const haystack = [
          rowName(row),
          row.customer?.phone ?? '',
          row.deposit.stripePaymentLinkId,
          studios[row.deposit.studioId]?.name ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [enriched, studioFilter, statusFilter, search, studios]);

  const totals = useMemo(() => {
    const acc: Record<DepositDisplayStatus, { count: number; total: number }> = {
      sent: { count: 0, total: 0 },
      viewed: { count: 0, total: 0 },
      paid: { count: 0, total: 0 },
      placed: { count: 0, total: 0 },
      lapsed: { count: 0, total: 0 },
      forfeited: { count: 0, total: 0 },
      expired: { count: 0, total: 0 },
      'link-resent': { count: 0, total: 0 },
    };
    for (const row of enriched) {
      if (studioFilter !== 'all' && row.deposit.studioId !== studioFilter) continue;
      acc[row.status].count += 1;
      acc[row.status].total += row.deposit.amountAud;
    }
    return acc;
  }, [enriched, studioFilter]);

  // K2: partial deposits (paid < sent) with their reminder-track state. Derived from
  // the ledger, so a fully-paid deposit drops off automatically.
  const topupRows = useMemo(
    () =>
      buildTopupTrack({
        nowIso: clockIso,
        deposits,
        bookings,
        customers,
        studioIds: studioFilter === 'all' ? undefined : [studioFilter],
      }),
    [clockIso, deposits, bookings, customers, studioFilter],
  );
  const topupOutstanding = topupRows.reduce((sum, r) => sum + r.outstandingAud, 0);

  return (
    <section className="px-8 py-8" data-testid="deposits-ledger">
      <header className="flex items-end justify-between max-w-7xl flex-wrap gap-4">
        <div>
          <p className="label-small-caps text-gold-bright">Operations</p>
          <h1 className="heading-display text-3xl text-cream mt-2">Deposit ledger</h1>
          <p className="font-serif text-cream-soft mt-3 max-w-2xl">
            Every Stripe payment request, live status, and the path from sent to placed. Filter by
            studio or status; click a row to jump to the customer record.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Log deposit button hidden for now */}
          {/* <button
            type="button"
            onClick={() => setLogOpen(true)}
            className="btn-primary text-xs"
            data-testid="deposits-log-manual"
          >
            <Banknote size={12} /> Log deposit
          </button> */}
          <label className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
            Studio
          </label>
          <select
            value={studioFilter}
            onChange={(e) => setStudioFilter(e.target.value as StudioId | 'all')}
            className="bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
            data-testid="deposits-studio-filter"
          >
            <option value="all">All studios</option>
            {studioList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.brand} · {s.name}
              </option>
            ))}
          </select>
        </div>
      </header>

      {logOpen ? <ManualDepositModal open onClose={() => setLogOpen(false)} /> : null}

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 max-w-7xl">
        {(['sent', 'viewed', 'paid', 'topups', 'placed', 'lapsed', 'forfeited', 'expired'] as const).map((s) => {
          if (s === 'topups') {
            const active = statusFilter === 'topups';
            return (
              <button
                key="topups"
                type="button"
                onClick={() => setStatusFilter(active ? 'all' : 'topups')}
                className={clsx(
                  'panel-dark text-left p-3 border transition',
                  active ? 'border-gold-bright shadow-gold-glow' : 'border-gold-dim/30 hover:border-gold-bright/60',
                )}
                data-testid="deposit-kpi-topups"
              >
                <p className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-inter uppercase tracking-widest text-gold-bright bg-gold-bright/10">
                  <Banknote size={9} /> Top-ups
                </p>
                <p className="font-cinzel text-2xl text-cream mt-2">{topupRows.length}</p>
                <p className="font-inter text-[11px] text-cream-soft uppercase tracking-widest mt-0.5">
                  {aud(topupOutstanding)}
                </p>
              </button>
            );
          }
          const meta = DEPOSIT_STATUS_META[s];
          const Icon = meta.icon;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
              className={clsx(
                'panel-dark text-left p-3 border transition',
                statusFilter === s
                  ? 'border-gold-bright shadow-gold-glow'
                  : 'border-gold-dim/30 hover:border-gold-bright/60',
              )}
              data-testid={`deposit-kpi-${s}`}
            >
              <p
                className={clsx(
                  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-inter uppercase tracking-widest',
                  meta.tone,
                )}
              >
                <Icon size={9} /> {meta.label}
              </p>
              <p className="font-cinzel text-2xl text-cream mt-2">{totals[s].count}</p>
              <p className="font-inter text-[11px] text-cream-soft uppercase tracking-widest mt-0.5">
                {aud(totals[s].total)}
              </p>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap max-w-7xl">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatusFilter(f.key)}
            className={clsx(
              'px-3 py-1.5 text-xs rounded-md border transition',
              statusFilter === f.key
                ? 'border-gold-bright text-gold-bright bg-gold/10'
                : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/40',
            )}
            data-testid={`deposit-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search
              size={12}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-cream-faint"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, phone, Stripe id"
              className="bg-ink-deep border border-gold-dim/40 rounded-md pl-7 pr-3 py-1.5 text-xs text-cream w-64 focus:outline-none focus:border-gold-bright"
              data-testid="deposits-search"
            />
          </div>
          <button
            type="button"
            className="btn-quiet text-xs"
            data-testid="deposits-export"
            title="Export ledger (mock)"
          >
            <Download size={12} /> Export
          </button>
        </div>
      </div>

      {statusFilter === 'topups' ? (
        <div className="mt-4 panel p-4 max-w-7xl" data-testid="deposits-topups">
          <div className="flex items-center gap-3 mb-3">
            <p className="label-small-caps text-gold-bright">Top-ups — awaiting</p>
            <span className="font-mono text-[11px] font-bold rounded-full px-2 py-0.5 bg-gold-bright text-ink-black">
              {topupRows.length}
            </span>
            <span className="font-inter text-[11px] text-cream-soft">{aud(topupOutstanding)} outstanding</span>
            {!readOnly && topupRows.some((r) => r.dueForReminder) ? (
              <button
                type="button"
                onClick={() => {
                  const due = topupRows.filter((r) => r.dueForReminder);
                  for (const r of due) sendTopupReminder(r.depositId);
                  pushNotification({
                    intensity: 'info',
                    title: 'Top-up reminders sent',
                    body: `${due.length} reminder${due.length === 1 ? '' : 's'} sent to clients with a balance due.`,
                    ttlMs: 3500,
                  });
                }}
                data-testid="deposits-topup-send-all"
                className="ml-auto btn-quiet text-xs whitespace-nowrap"
              >
                <Mail size={12} /> Send all due
              </button>
            ) : null}
          </div>
          {topupRows.length === 0 ? (
            <p className="font-serif text-cream-soft text-sm py-6 text-center">
              No top-ups awaiting. Partial deposits appear here until the balance is paid in full.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left">
                    <ThTU>Customer</ThTU>
                    <ThTU right>Sent</ThTU>
                    <ThTU right>Paid</ThTU>
                    <ThTU right>Outstanding</ThTU>
                    <ThTU>Session</ThTU>
                    <ThTU right>Days</ThTU>
                    <ThTU>Last reminder</ThTU>
                    <ThTU>Track</ThTU>
                    {!readOnly ? <ThTU>Action</ThTU> : null}
                  </tr>
                </thead>
                <tbody>
                  {topupRows.map((r) => (
                    <tr
                      key={r.depositId}
                      className="border-t border-gold-dim/20"
                      data-testid={`deposits-topup-row-${r.depositId}`}
                    >
                      <td className="py-2 pr-3 text-cream">{r.customerName}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums text-cream-faint">{aud(r.sentAud)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums text-cream-soft">{aud(r.paidAud)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums text-gold-bright font-semibold">
                        {aud(r.outstandingAud)}
                      </td>
                      <td className="py-2 pr-3 text-cream-soft whitespace-nowrap text-[12.5px]">
                        {r.sessionIso ? format(new Date(r.sessionIso), 'EEE d MMM') : '—'}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums text-cream-soft">
                        {r.daysUntilSession ?? '—'}
                      </td>
                      <td className="py-2 pr-3 text-cream-faint text-[12px] whitespace-nowrap">
                        {r.lastReminderAt ? format(new Date(r.lastReminderAt), 'd MMM') : '—'}
                      </td>
                      <td className="py-2 pr-3">
                        <TopupTrackChip state={r.state} />
                      </td>
                      {!readOnly ? (
                        <td className="py-2 pr-3">
                          <button
                            type="button"
                            onClick={() => {
                              sendTopupReminder(r.depositId);
                              pushNotification({
                                intensity: 'info',
                                title: 'Reminder sent',
                                body: `Top-up reminder sent to ${r.customerName}.`,
                                ttlMs: 3000,
                              });
                            }}
                            data-testid={`deposits-topup-send-${r.depositId}`}
                            className="btn-quiet text-xs whitespace-nowrap"
                          >
                            <Mail size={11} /> Send reminder
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="font-inter text-[11px] text-cream-faint mt-3">
            Outstanding = sent − paid, from the movement ledger. A top-up recalculates the row; paid in full removes it.
          </p>
        </div>
      ) : (
      <div className="mt-4 panel p-0 max-w-7xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="deposits-table">
          <thead>
            <tr className="text-left text-cream-faint font-inter text-[10px] uppercase tracking-widest">
              <th className="px-4 py-3 font-normal">Customer</th>
              <th className="px-4 py-3 font-normal">Mobile</th>
              <th className="px-4 py-3 font-normal">Studio</th>
              <th className="px-4 py-3 font-normal">Sent by</th>
              <th className="px-4 py-3 font-normal">Stripe ref</th>
              <th className="px-4 py-3 font-normal text-right">Amount</th>
              <th className="px-4 py-3 font-normal">Created</th>
              <th className="px-4 py-3 font-normal">Status</th>
              <th className="px-4 py-3 font-normal">Booking</th>
              <th className="px-4 py-3 font-normal">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="px-4 py-10 text-center font-serif text-cream-soft"
                >
                  No deposits match these filters.
                </td>
              </tr>
            ) : null}
            {filtered.map((row) => {
              const meta = DEPOSIT_STATUS_META[row.status];
              const Icon = meta.icon;
              const studio = studios[row.deposit.studioId];
              const linked = row.deposit.bookingId
                ? bookings[row.deposit.bookingId]
                : undefined;
              const canResend = ['sent', 'viewed', 'expired', 'link-resent'].includes(row.status);
              return (
                <Fragment key={row.deposit.id}>
                <tr
                  onClick={() => {
                    if (!row.customer) return;
                    navigate(`/customer/${row.customer.id}`, {
                      state: { fromLabel: 'deposit ledger' },
                    });
                  }}
                  className="border-t border-gold-dim/15 cursor-pointer hover:bg-ink-soft/40 transition"
                  data-testid={`deposit-row-${row.deposit.id}`}
                >
                  <td className="px-4 py-3 font-inter text-cream">
                    {rowName(row)}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-cream-soft">
                    {row.customer?.phone ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-cream-soft">
                    {studio ? `${studio.brand} · ${studio.name}` : row.deposit.studioId}
                  </td>
                  <td className="px-4 py-3" data-testid={`deposit-origin-${row.deposit.id}`}>
                    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-inter uppercase tracking-widest',
                      depositOrigin(row.deposit) === 'artist' ? 'bg-gold/15 text-gold-bright' : 'bg-cream-soft/15 text-cream-soft')}>
                      {depositOrigin(row.deposit) === 'artist' ? 'Artist' : 'Office'}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-cream-faint truncate max-w-[180px]">
                    {row.deposit.method ? (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-gold/15 text-gold-bright text-[9px] uppercase tracking-widest"
                        data-testid={`deposit-method-${row.deposit.id}`}
                      >
                        <Banknote size={9} /> {METHOD_LABEL[row.deposit.method] ?? row.deposit.method}
                      </span>
                    ) : (
                      `${row.deposit.stripePaymentLinkId.slice(0, 24)}…`
                    )}
                  </td>
                  <td className="px-4 py-3 font-inter text-cream text-right">
                    {aud(row.deposit.amountAud)}
                  </td>
                  <td className="px-4 py-3 font-inter text-[11px] text-cream-soft uppercase tracking-widest">
                    {simTimeFull(row.deposit.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span
                        className={clsx(
                          'inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-inter uppercase tracking-widest',
                          meta.tone,
                        )}
                      >
                        <Icon size={10} /> {meta.label}
                      </span>
                      {row.deposit.supersededByDepositId ? (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-inter uppercase tracking-widest bg-cream-soft/15 text-cream-soft"
                          data-testid={`deposit-resent-${row.deposit.id}`}
                          title="A fresh link was sent for this expired deposit"
                        >
                          <RotateCw size={9} /> Link resent
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3" data-testid={`deposit-booking-${row.deposit.id}`}>
                    {(() => {
                      if (linked) {
                        const artist = artists[linked.artistId];
                        return (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/calendar/${linked.studioId}`, {
                                state: {
                                  focusDayIso: linked.start,
                                  focusArtistId: linked.artistId,
                                },
                              });
                            }}
                            className="text-left text-mint hover:text-gold-bright transition"
                          >
                            <p className="font-inter text-xs">
                              {simTimeFull(linked.start)}
                            </p>
                            <p className="font-inter text-[10px] uppercase tracking-widest text-cream-soft mt-0.5">
                              {artist?.displayName ?? linked.artistId} ·{' '}
                              {linked.sessionLength.replace('-', ' ')}
                            </p>
                          </button>
                        );
                      }
                      if (row.status === 'paid' && !readOnly) {
                        return (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/calendar/${row.deposit.studioId}`, {
                                state: { placeBookingDepositId: row.deposit.id },
                              });
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-inter uppercase tracking-widest bg-blood/20 text-blood hover:bg-blood/30 transition animate-ring-pulse"
                            data-testid={`deposit-place-cta-${row.deposit.id}`}
                          >
                            Awaiting placement · place now
                          </button>
                        );
                      }
                      return (
                        <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                          ·
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3" data-testid={`deposit-action-${row.deposit.id}`}>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(row.status === 'lapsed' || row.status === 'forfeited') && canReinstate && !readOnly ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            reinstateDeposit(row.deposit.id, persona ?? 'system');
                            pushNotification({
                              intensity: 'info',
                              title: 'Deposit reinstated',
                              body: `${rowName(row)}'s deposit is back in the money queue.`,
                              ttlMs: 3500,
                            });
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-inter uppercase tracking-widest border border-azure/50 text-azure hover:border-azure hover:bg-azure/10 transition"
                          data-testid={`deposit-reinstate-${row.deposit.id}`}
                          title="Reinstate this forfeited deposit"
                        >
                          <Undo2 size={10} /> Reinstate
                        </button>
                      ) : null}
                      {!readOnly && canResend ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            resendLink({ customer: row.customer, deposit: row.deposit });
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-inter uppercase tracking-widest border border-gold-dim/40 text-gold-bright hover:border-gold-bright hover:bg-gold/10 transition"
                          data-testid={`deposit-resend-${row.deposit.id}`}
                          title="Resend deposit link"
                        >
                          <RotateCw size={10} /> Resend link
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedId(expandedId === row.deposit.id ? null : row.deposit.id);
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-inter uppercase tracking-widest border border-gold-dim/40 text-cream-soft hover:border-gold-bright/60 hover:text-gold-bright transition"
                        data-testid={`deposit-movements-toggle-${row.deposit.id}`}
                        title="Show deposit movement history"
                      >
                        <History size={10} /> Movements
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedId === row.deposit.id ? (
                  <tr data-testid={`deposit-drawer-${row.deposit.id}`}>
                    <td colSpan={10} className="px-4 pb-4 bg-ink-soft/20">
                      <DepositMovementDrawer deposit={row.deposit} />
                    </td>
                  </tr>
                ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </section>
  );
}

function ThTU({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={clsx(
        'font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3',
        right ? 'text-right' : '',
      )}
    >
      {children}
    </th>
  );
}

const TOPUP_TRACK_META: Record<TopupTrackState, { label: string; cls: string }> = {
  due: { label: 'Reminder due', cls: 'text-gold border-gold/40 bg-gold/10' },
  'final-due': { label: 'Final due · T-7', cls: 'text-blood border-blood/40 bg-blood/10' },
  waiting: { label: 'Waiting', cls: 'text-cream-soft border-gold-dim/30 bg-ink-soft/40' },
  suppressed: { label: 'Suppressed · replied', cls: 'text-mint border-mint/40 bg-mint/10' },
};

function TopupTrackChip({ state }: { state: TopupTrackState }) {
  const m = TOPUP_TRACK_META[state];
  return (
    <span className={clsx('inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold', m.cls)}>
      {m.label}
    </span>
  );
}
